import Loader from '@/components/Loader';
import TableBody from './TableBody';
import TableHeader from './header/TableHeader';
import { useTableSort } from '@/hooks/tables/useTableSort';
import type { DataTableProps } from '@/types';
import { TablePagination } from '@acast-tech/decibel';
import { useTablePagination } from '@/hooks/tables/useTablePagination';
import { twMerge } from 'tailwind-merge';

const DEFAULT_PAGINATION = {
  summary: true,
  pageSizeOptions: [5, 10, 15, 20],
  defaultPageSize: 5,
};

/**
 * This component is used to display the data table.
 * @param headerData - The header data.
 * @param tableData - The table data.
 * @param isLoading - Whether the table is loading.
 * @param initialState - Configuration for controlling the initial state of the table.
 * @param pagination - The pagination configuration. false to disable pagination, otherwise use Partial<PaginationProps>
 * @param tableWrapperClassName - The class name for the container of the table.
 * @param dataTestId - The data test id for the table.
 * @param onSort - A callback function after a sort is applied.
 * @param onPageChange - The callback function after a page is changed.
 * @returns The data table component.
 */
const DataTable = ({
  headerData,
  tableData,
  isLoading = false,
  initialState,
  dataTestId,
  pagination: customPagination = false,
  tableWrapperClassName = '',
  onSort,
  onPageChange,
  renderRow,
  serverSideTotal,
}: DataTableProps) => {
  const shouldPaginate = !!customPagination;
  const isServerSide = serverSideTotal !== undefined;
  const numberOfItems = isServerSide ? serverSideTotal : tableData.length;
  const pagination = { ...DEFAULT_PAGINATION, ...(customPagination || {}) };
  const { sortedData, sortField, handleSort } = useTableSort(
    tableData,
    onSort,
    initialState?.sorting
  );

  const { page, pageSize, pageStart, pageEnd, setPage, setPageSize } = useTablePagination(
    numberOfItems,
    pagination
  );

  const { summary, pageSizeOptions } = pagination;
  const pageData = isServerSide
    ? tableData
    : sortedData.slice(pageStart, shouldPaginate ? pageEnd : numberOfItems);

  const handlePageChange = (page: number) => {
    setPage(page);

    onPageChange?.(page);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[25dvh]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid={dataTestId}>
      <div className={twMerge('flex flex-col', tableWrapperClassName)}>
        <table className="w-full">
          <TableHeader headerData={headerData} onSort={handleSort} sortField={sortField} />
          <TableBody data={pageData} renderRow={renderRow} />
        </table>
        {numberOfItems === 0 && (
          <div className="NoDataContainer w-full items-center justify-center flex text-gray-500 my-5">
            No data
          </div>
        )}
      </div>
      {numberOfItems > 0 && shouldPaginate && (
        <TablePagination
          total={numberOfItems}
          current={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
          pageSizeOptions={pageSizeOptions}
          summary={summary}
          data-test-id="DataTable__pagination"
        />
      )}
    </div>
  );
};

export default DataTable;
